import React, {ReactNode} from 'react'
import {Tag as ChakraTag, TagLabel as ChakraTagLabel, TagProps as ChakraTagProps} from '@chakra-ui/react'

interface TagProps extends Omit<ChakraTagProps, 'size' | 'colorScheme' | 'variant' | 'borderRadius'> {
    size?: 'sm' | 'md'
    colorScheme?: 'light' | 'dark'
    variant?: 'opacity'
    borderRadius?: string
    children: ReactNode
}

/**
 * Tag Component is a wrapper around the Chakra UI Tag component.
 *
 * @param {TagProps} props - Properties of the component.
 * @param {'sm'} [props.size='md'] - Size of the tag. Default is 'md'.
 * @param {'light' | 'dark'} [props.colorScheme='light'] - Color scheme of the tag. Default is 'light'.
 * @param {'opacity'} [props.variant] - Variant of the tag
 * @param {string} [props.borderRadius='full'] - Border radius of the tag. Default is 'full'.
 * @param {ReactNode} props.children - The content to be displayed inside the tag.
 * @returns {JSX.Element} The rendered Tag component.
 */
const Tag = ({size = 'md', colorScheme = 'light', variant, borderRadius = 'full', children, ...props}: TagProps): JSX.Element => {
    return (
        <ChakraTag data-testid="tag" size={size} borderRadius={borderRadius} variant={variant} colorScheme={colorScheme} {...props}>
            <ChakraTagLabel>{children}</ChakraTagLabel>
        </ChakraTag>
    )
}

export {Tag, TagProps}
